import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Filter } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Store = () => {
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState('');

  useEffect(() => {
    loadStoreData();
  }, []);

  const loadStoreData = async () => {
    try {
      const [productsRes, bundlesRes] = await Promise.all([
        axios.get(`${API}/store/products`),
        axios.get(`${API}/store/bundles`)
      ]);
      setProducts(productsRes.data);
      setBundles(bundlesRes.data);
    } catch (error) {
      console.error('Error loading store data:', error);
      toast.error('Failed to load store data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId, productType, title) => {
    setAdding(productId);
    const success = await addToCart(productId, productType, 1);
    if (success) {
      toast.success(`${title} added to cart!`);
    } else {
      toast.error('Failed to add to cart');
    }
    setAdding('');
  };

  const getProductsByCategory = (category) => {
    return products.filter(p => p.category === category);
  };

  const ProductCard = ({ product }) => (
    <Card className="overflow-hidden hover:shadow-xl transition-all">
      <div className="aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
      </div>
      <CardHeader>
        <CardTitle className="text-lg line-clamp-2">{product.title}</CardTitle>
        <CardDescription className="line-clamp-2">{product.description}</CardDescription>
      </CardHeader>
      <CardContent>
        {product.scripture && (
          <p className="text-sm text-blue-600 mb-2">{product.scripture}</p>
        )}
        {product.format && (
          <p className="text-sm text-gray-500">{product.format}</p>
        )}
        {product.size && (
          <p className="text-sm text-gray-500">{product.size}</p>
        )}
      </CardContent>
      <CardFooter className="flex justify-between items-center">
        <span className="text-2xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
        <Button 
          onClick={() => handleAddToCart(product.id, 'product', product.title)}
          disabled={adding === product.id || !product.in_stock}
        >
          <ShoppingCart className="mr-2 h-4 w-4" />
          {adding === product.id ? 'Adding...' : !product.in_stock ? 'Out of Stock' : 'Add to Cart'}
        </Button>
      </CardFooter>
    </Card>
  );

  const BundleCard = ({ bundle }) => (
    <Card className="border-2 hover:border-amber-500 transition-all relative overflow-hidden">
      {bundle.badge && (
        <Badge className="absolute top-4 right-4 bg-amber-600 text-white z-10">
          {bundle.badge}
        </Badge>
      )}
      <div className="aspect-video overflow-hidden bg-gray-100">
        <img
          src={bundle.image}
          alt={bundle.title}
          className="w-full h-full object-cover"
        />
      </div>
      <CardHeader>
        <CardTitle className="text-xl">{bundle.title}</CardTitle>
        <CardDescription>{bundle.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-900">${bundle.price.toFixed(2)}</span>
          <span className="text-lg text-gray-400 line-through">${bundle.original_price.toFixed(2)}</span>
        </div>
        <p className="text-green-600 font-semibold">Save ${bundle.savings.toFixed(2)}!</p>
        <div className="border-t pt-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Includes:</p>
          <ul className="space-y-2 text-sm text-gray-600">
            {bundle.includes.map((item, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          className="w-full bg-amber-600 hover:bg-amber-700"
          onClick={() => handleAddToCart(bundle.id, 'bundle', bundle.title)}
          disabled={adding === bundle.id}
        >
          {adding === bundle.id ? 'Adding...' : 'Get This Bundle'}
        </Button>
      </CardFooter>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-xl text-gray-600">Loading store...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Ministry Store</h1>
            <p className="text-xl text-gray-300">
              Books, shofars, biblical apothecary, and curriculum bundles
            </p>
          </div>
        </div>
      </section>

      {/* Store Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="all" className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="grid grid-cols-5 w-full max-w-3xl">
                <TabsTrigger value="all">All Products</TabsTrigger>
                <TabsTrigger value="books">Books</TabsTrigger>
                <TabsTrigger value="shofars">Shofars</TabsTrigger>
                <TabsTrigger value="apothecary">Apothecary</TabsTrigger>
                <TabsTrigger value="bundles">Bundles</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="all" className="space-y-12">
              {/* Featured Bundles */}
              {bundles.filter(b => b.featured).length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Featured Bundles</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {bundles.filter(b => b.featured).map((bundle) => (
                      <BundleCard key={bundle.id} bundle={bundle} />
                    ))}
                  </div>
                </div>
              )}

              {/* Books */}
              {getProductsByCategory('books').length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Books</h2>
                  <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {getProductsByCategory('books').map((book) => (
                      <ProductCard key={book.id} product={book} />
                    ))}
                  </div>
                </div>
              )}

              {/* Shofars */}
              {getProductsByCategory('shofars').length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Shofars</h2>
                  <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {getProductsByCategory('shofars').map((shofar) => (
                      <ProductCard key={shofar.id} product={shofar} />
                    ))}
                  </div>
                </div>
              )}

              {/* Apothecary */}
              {getProductsByCategory('apothecary').length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Biblical Apothecary</h2>
                  <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {getProductsByCategory('apothecary').map((item) => (
                      <ProductCard key={item.id} product={item} />
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="books">
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {getProductsByCategory('books').map((book) => (
                  <ProductCard key={book.id} product={book} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="shofars">
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {getProductsByCategory('shofars').map((shofar) => (
                  <ProductCard key={shofar.id} product={shofar} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="apothecary">
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {getProductsByCategory('apothecary').map((item) => (
                  <ProductCard key={item.id} product={item} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="bundles">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bundles.map((bundle) => (
                  <BundleCard key={bundle.id} bundle={bundle} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">
              Not sure where to start?
            </h2>
            <p className="text-xl text-gray-600">
              Check out our Starter Bundle - includes Little Book 2 + GIT Lesson 1 + 30-day digital trial
            </p>
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
              View Starter Bundle
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Store;
